/**
 * ServiceDetailPage Component
 * Service detail view rendered inside the concierge panel (not full-screen)
 * Keyboard navigation: Arrow keys to navigate options, Escape to go back, Enter to select
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ServiceDetail, ServiceOption } from '@/types';

interface ServiceDetailPageProps {
  service: ServiceDetail;
  onBack: () => void;
  onSelectOption: (option: ServiceOption) => void;
  isActive: boolean;
  /** P2.7-2: Pre-select an option by optionId (from voice booking command) */
  initialOptionId?: string;
}

export function ServiceDetailPage({
  service,
  onBack,
  onSelectOption,
  isActive,
  initialOptionId,
}: ServiceDetailPageProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const initialOptionProcessedRef = useRef(false);

  const availableOptions = service.options.filter(opt => opt.available);

  // Auto-advance image slideshow
  useEffect(() => {
    if (service.images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % service.images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [service.images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onBack();
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedOptionIndex(prev =>
            prev > 0 ? prev - 1 : availableOptions.length - 1
          );
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedOptionIndex(prev =>
            prev < availableOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setCurrentImageIndex(prev =>
            prev > 0 ? prev - 1 : service.images.length - 1
          );
          break;
        case 'ArrowRight':
          e.preventDefault();
          setCurrentImageIndex(prev =>
            (prev + 1) % service.images.length
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (availableOptions[selectedOptionIndex]) {
            onSelectOption(availableOptions[selectedOptionIndex]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, selectedOptionIndex, availableOptions, service.images.length, onBack, onSelectOption]);

  // Focus container on mount
  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isActive]);

  // P2.7-2: Auto-select a voice-specified option once when the view becomes active
  useEffect(() => {
    if (!initialOptionId || initialOptionProcessedRef.current || !isActive) return;
    const option = availableOptions.find(o => o.optionId === initialOptionId);
    if (option) {
      initialOptionProcessedRef.current = true;
      setTimeout(() => onSelectOption(option), 150);
    }
  }, [isActive, initialOptionId, availableOptions, onSelectOption]);

  const formatPrice = (price: number | null) => {
    if (price === null) return 'Free';
    return `€${price}`;
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return null;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${minutes}min`;
  };

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="service-detail h-full w-full bg-zinc-950 flex flex-col outline-none overflow-hidden rounded-xl"
    >
      {/* Location & Hours info bar */}
      {(service.location || service.operatingHours) && (
        <div className="service-info-bar flex-shrink-0 px-4 py-2 bg-black/30 flex items-center justify-between text-xs">
          {service.location && (
            <span className="text-white/50">📍 {service.location}</span>
          )}
          {service.operatingHours && (
            <span className="text-white/50">🕐 {service.operatingHours}</span>
          )}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Image slideshow - left side */}
        <div className="w-2/5 relative overflow-hidden flex-shrink-0">
          <AnimatePresence mode="wait">
            <img
              key={currentImageIndex}
              src={service.images[currentImageIndex]}
              alt={service.title}
              className="absolute inset-0 w-full h-full object-cover slide-img-fade"
            />
          </AnimatePresence>

          {/* Image indicators */}
          {service.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {service.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentImageIndex
                      ? 'bg-white w-8'
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Navigation hint */}
          <div className="absolute bottom-4 left-4 text-white/50 text-xs">
            ← → images
          </div>
        </div>

        {/* Options panel - right side */}
        <div className="w-3/5 bg-zinc-900 flex flex-col overflow-hidden">
          {/* Description */}
          <div className="service-desc-panel p-4 border-b border-white/10 flex-shrink-0">
            <p className="text-white/70 text-sm leading-relaxed line-clamp-3">
              {service.longDescription}
            </p>
          </div>

          {/* Options grid */}
          <div className="service-options-panel flex-1 overflow-y-auto p-4 min-h-0">
            <h2 className="text-white/50 text-xs uppercase tracking-wider mb-3">
              Select an option
            </h2>
            <div className="space-y-2">
              {availableOptions.map((option, idx) => (
                <motion.div
                  key={option.optionId}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => {
                    setSelectedOptionIndex(idx);
                    onSelectOption(option);
                  }}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    idx === selectedOptionIndex
                      ? 'bg-teal-600/30 ring-2 ring-teal-500'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium text-sm">
                        {option.name}
                      </h3>
                      <p className="text-white/50 text-xs mt-0.5 truncate">
                        {option.description}
                      </p>
                      {option.durationMinutes && (
                        <p className="text-white/40 text-xs mt-1">
                          ⏱ {formatDuration(option.durationMinutes)}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <span className={`text-base font-semibold ${
                        option.price === null ? 'text-green-400' : 'text-teal-400'
                      }`}>
                        {formatPrice(option.price)}
                      </span>
                    </div>
                  </div>

                  {idx === selectedOptionIndex && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between text-xs"
                    >
                      <span className="text-white/40">Press Enter to book</span>
                      <span className="text-teal-400">→</span>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>


        </div>
      </div>
    </motion.div>
  );
}

export default ServiceDetailPage;
